import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { useState } from 'react';
import { api, unwrap } from '../../../api/client';
import type { AssetDetail } from '../../../api/types';
import { AssetLink } from '../../../components/assets/AssetLink';
import { Card, EmptyState, ErrorBox, Loading, Pagination, Select } from '../../../components/ui';
import { changeLines, describeEvent } from '../../../lib/events';
import { EVENT_LABELS, formatDate, formatDateTime } from '../../../lib/format';

const FILTERS: Array<{ label: string; types: string }> = [
  { label: 'Alle Ereignisse', types: '' },
  { label: 'Mitarbeiter', types: 'employee_assigned,employee_unassigned' },
  { label: 'Standort', types: 'location_changed' },
  { label: 'Status', types: 'status_changed,asset_disposed' },
  { label: 'Ein-/Ausbau', types: 'component_installed,component_removed,component_moved,child_removed,part_added,part_removed,part_updated' },
  { label: 'Stammdaten', types: 'asset_created,asset_updated,specs_changed' },
  { label: 'Dateien', types: 'document_uploaded,photo_uploaded,primary_photo_changed,attachment_updated,attachment_deleted' },
];

export function HistoryTab({ asset }: { asset: AssetDetail }) {
  const [page, setPage] = useState(1);
  const [filter, setFilter] = useState('');
  const pageSize = 25;
  const q = useQuery({
    queryKey: ['asset', asset.id, 'history', page, filter],
    queryFn: () =>
      unwrap(api.GET('/api/assets/{ref}/history', { params: { path: { ref: asset.id }, query: { page, page_size: pageSize, event_type: filter || undefined } } })),
    placeholderData: keepPreviousData,
  });

  return (
    <Card>
      <div className="flex items-center justify-between gap-2 border-b border-slate-200 px-4 py-3">
        <h2 className="text-sm font-semibold">Lückenlose Historie</h2>
        <div className="w-56">
        <Select
          aria-label="Ereignisse filtern"
          value={filter}
          onChange={(e) => {
            setFilter(e.target.value);
            setPage(1);
          }}
        >
          {FILTERS.map((f) => (
            <option key={f.label} value={f.types}>
              {f.label}
            </option>
          ))}
        </Select>
        </div>
      </div>
      {q.isPending ? (
        <Loading />
      ) : q.error ? (
        <ErrorBox error={q.error} className="m-4" />
      ) : q.data.data.length === 0 ? (
        <EmptyState title="Keine Einträge" />
      ) : (
        <>
          <ol className="divide-y divide-slate-100">
            {q.data.data.map((e) => {
              const lines = changeLines(e);
              return (
                <li key={e.id} className="flex gap-4 px-4 py-3">
                  <div className="w-28 shrink-0 text-xs text-slate-500">
                    <div className="font-medium text-slate-700">{formatDate(e.effective_date ?? e.occurred_at)}</div>
                    {e.effective_date && e.effective_date !== e.occurred_at.slice(0, 10) && <div title="erfasst am">erf. {formatDateTime(e.occurred_at)}</div>}
                  </div>
                  <div className="min-w-0 flex-1 text-sm">
                    <div className="flex flex-wrap items-center gap-x-2">
                      <span className="font-medium text-slate-900">{EVENT_LABELS[e.event_type] ?? e.event_type}</span>
                      {e.related_asset && e.perspective === 'related' && (
                        <span className="text-xs text-slate-500">
                          (betrifft <AssetLink asset={e.related_asset} />)
                        </span>
                      )}
                    </div>
                    <div className="text-slate-600">{describeEvent(e)}</div>
                    {lines.length > 0 && (
                      <ul className="mt-1 space-y-0.5 text-xs text-slate-500">
                        {lines.map((l) => (
                          <li key={l.label}>
                            {l.label}: <span className="line-through decoration-slate-300">{l.from}</span> → <span className="text-slate-700">{l.to}</span>
                          </li>
                        ))}
                      </ul>
                    )}
                    {typeof (e.metadata as Record<string, unknown> | null)?.note === 'string' && (
                      <div className="mt-1 text-xs text-slate-500 italic">„{String((e.metadata as Record<string, unknown>).note)}“</div>
                    )}
                  </div>
                  <div className="hidden w-40 shrink-0 text-right text-xs text-slate-500 sm:block">{e.actor.label ?? 'System'}</div>
                </li>
              );
            })}
          </ol>
          <Pagination page={page} pageSize={pageSize} total={q.data.total} onPage={setPage} />
        </>
      )}
    </Card>
  );
}
